import {SimpleSignal} from "../utils/SimpleSignal";
import {ISceneAddon} from "./addon/ISceneAddon";
import {Config} from "../config/Config";
import {SceneNodeState} from "./ISceneNode";
import {Main} from "../Main";
import {WallAddon} from "./addon/WallAddon";
import {Building, WallSide} from "../building/Building";
import {WarningWindow} from "../ui/WarningWindow";
import {ConfirmWindow} from "../ui/ConfirmWindow";

export class Scene
{
	//public placeNodeSignal:utils.SimpleSignal;
	//public addNodeSignal:utils.SimpleSignal;

	public changeSignal: SimpleSignal;

	public placingAddon: ISceneAddon;

	public config: Config;
	public building: Building;
	private _addons: ISceneAddon[];

	constructor(config: Config)
	{
		this.config = config;
		this._addons = [];

		this.changeSignal = new SimpleSignal();
	}

	public async addPlacingAddon(dispatch = true, isTheUserManuallyPlacing = true): Promise<boolean> // isTheUserManuallyPlacing -> when you load a project that has rollups, don't ask!
	{
		const placingAddon = this.placingAddon;

		placingAddon.state = SceneNodeState.PLACED;
		if (dispatch)
		{
			var pos = placingAddon.getPosition();
			var whichWall = pos.wall;
			var isThatWallEmpty = !Main.getInstance().config.building.leanto[whichWall].enabled.value; // check if there's a leanto on this side

			if (placingAddon.itemId.indexOf("rollup") !== -1 && isTheUserManuallyPlacing)
			{
				if (isThatWallEmpty && (whichWall === "front" || whichWall === "back"))
				{
					var addons = Main.getInstance().scene.building.addons[whichWall];
					var addonsCount = addons.length;
					var width = placingAddon.width;


					var isCenterAValidSpacing = true;

					for (var i = 0; i < addonsCount; ++i)
					{
						var leftEndOfAlreadyPlacedAddon = addons[i].getPosition().horizontal - addons[i].width / 2 - 2; // 2 for the padding
						var rightEndOfAlreadyPlacedAddon = addons[i].getPosition().horizontal + addons[i].width / 2 + 2; // 2 for the padding

						var potentialLeftEndOfCurrentAddon = - width / 2;
						var potentialRightEndOfCurrentAddon = + width / 2;

						if (potentialLeftEndOfCurrentAddon < rightEndOfAlreadyPlacedAddon && potentialRightEndOfCurrentAddon > leftEndOfAlreadyPlacedAddon)
						{
							isCenterAValidSpacing = false;
							break;
						}
					}

					if (isCenterAValidSpacing)
					{
						if (await ConfirmWindow.open(`Do you want to place this rollup door in the center?`))
						{
							pos.horizontal = 0;
							placingAddon.setPosition(pos);
						}
					}
				}
			}

			if (placingAddon.itemId.indexOf('window') === -1 && isTheUserManuallyPlacing)
			{
				if (!this.isAddonPlaceValid(placingAddon)) // We call a rollup place valid if it's height is at least 2 feet lower than the wall it is placed on.
				{
					await WarningWindow.open(`This component will not fit due to height restrictions within the wall. Please choose a different size.`);
					this.cancelPlacing();

					return false; // if we don't return, we dispatch "placed" signal which is wrong in this case.
				}
			}

			this.changeSignal.dispatch(SceneNodeState.PLACED, placingAddon, isTheUserManuallyPlacing);
		}

		// only set this after the signal is dispatched because
		// NodePlacer and ScenePanel check this flag
		placingAddon.added = true;
		this.placingAddon = null;

		return true;
	}

	public isAddonPlaceValid(addon: ISceneAddon): boolean
	{
		if (addon instanceof WallAddon) // in theory, this is always true
		{
			const pos = addon.getPosition();
			const whichWall = pos.wall as WallSide;
			const addonWidth = addon.width;
			const addonHeight = WallAddon.getTopOfAddonWithOffset(addon);
			const isBuildingSloped = Main.getInstance().config.building.single_slope.value;

			const buildingWidth = Main.getInstance().config.building.width.value;
			const buildingHeight = Main.getInstance().config.building.height.value;
			const pitch = Main.getInstance().config.building.pitch.value;

			if (whichWall === "left" || (whichWall && !isBuildingSloped))
			{
				if (buildingHeight < addonHeight) // left side is not dependant of single sloping
				{
					return false;
				}
			}

			if (isBuildingSloped)
			{
				if (whichWall === "front" || whichWall === "back")
				{
					var buildingHeightOnRollupEdge_1 = buildingHeight + (buildingWidth / 2 + pos.horizontal - addonWidth / 2) * pitch / 12;
					var buildingHeightOnRollupEdge_2 = buildingHeight + (buildingWidth / 2 + pos.horizontal + addonWidth / 2) * pitch / 12;

					if (Math.min(buildingHeightOnRollupEdge_1, buildingHeightOnRollupEdge_2) < addonHeight)
					{
						return false;
					}
				}
				else if (whichWall)
				{
					var buildingTotalHeight = buildingHeight + buildingWidth * pitch / 12;
					if (buildingTotalHeight < addonHeight)
					{
						return false;
					}
				}
			}

			//nonsloped front and back

			if (!isBuildingSloped)
			{
				if (whichWall === "front" || whichWall === "back")
				{
					var buildingTop = buildingHeight + (buildingWidth / 2) * pitch / 12;
					var buildingHeightOnRollupEdge_1 = buildingTop - Math.abs((pos.horizontal - addonWidth / 2) * pitch / 12);
					var buildingHeightOnRollupEdge_2 = buildingTop - Math.abs((pos.horizontal + addonWidth / 2) * pitch / 12);

					if (Math.min(buildingHeightOnRollupEdge_1, buildingHeightOnRollupEdge_2) < addonHeight)
					{
						return false;
					}
				}
			}

			// leanto
			if (this.building.leanToManager.isThereAConflictWithAddon(whichWall, addon))
			{
				return false;
			}
		}

		return true;
	}

	public startPlacing(addon: ISceneAddon)
	{
		// The addon might already be added to addons (when we're moving an already existing
		// addon), so we have to check if it's already added.

		if (this._addons.indexOf(addon) === -1)
		{
			this._addons.push(addon);
		}

		Main.getInstance().collapseSideBar();

		this.placingAddon = addon;
		addon.state = SceneNodeState.PLACING;
		this.changeSignal.dispatch(SceneNodeState.PLACING, addon);
	}

	public cancelPlacing()
	{
		var placingNode = this.placingAddon;
		if (!placingNode.added)
		{
			this.removePlacingAddon();
		}
		this.placingAddon = null;

		this.changeSignal.dispatch(SceneNodeState.CANCELED, placingNode);
	}

	private removePlacingAddon()
	{
		if (this.placingAddon)
		{
			var index = this._addons.indexOf(this.placingAddon);
			if (index > -1)
			{
				this._addons.splice(index, 1);
			}
		}
	}

	/**
	 * Called when the user deletes an addon.
	 * @param addon
	 */
	public destroyAddon(addon: ISceneAddon)
	{
		var index = this._addons.indexOf(addon);
		if (index > -1)
		{
			this._addons.splice(index, 1);
		}

		this.changeSignal.dispatch(SceneNodeState.DESTROYED, addon);
	}

	public destroyAddons()
	{
		var addons = this._addons;
		while (addons.length > 0)
		{
			var addon = addons.shift();
			this.destroyAddon(addon);
		}
	}

	public findAddonById(id: string)
	{
		for (var i = 0; i < this._addons.length; i++)
		{
			if (this._addons[i].id === id)
			{
				return this._addons[i];
			}
		}
		return null;
	}

	public updateAddonPositions()
	{
		for (var i = 0; i < this._addons.length; i++)
		{
			this._addons[i].updatePosition();
		}
	}

	// called when an addon is added / removed
	// refreshes the model's addon property (that's used to save the project)
	public getAddonsForSave()
	{
		return this._addons.filter(addon => addon.hasValidPlace).map(addon =>
		{
			return {
				itemId: addon.itemId,
				type: addon.type,
				position: addon.getPosition()
			}
		});;
	}

	public highlightNode(node: WallAddon)
	{
		const side = node.getPosition().wall;
		Main.getInstance().content.controller.onViewPresetChange(side);

		node.highlight();
	}

	/*
	public get building()
	{
		return this._building;
	}
	*/

	public get addons()
	{
		return this._addons;
	}
}
